import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { fetchVehicleSources } from "../../../../services/vehicleSourceApi";
import {
  confirmIdentityMatch,
  fetchVehicleDuplicateCandidates,
  rejectIdentityMatch,
} from "../../../../services/vehicleDuplicateApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RowActionMenu from "../layout/RowActionMenu";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const VehicleDuplicateReviewPage = ({ onClose, onOpenCanonical, onOpenListing }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const [matchStatus, setMatchStatus] = useState("");
  const [reviewRequired, setReviewRequired] = useState("");
  const [vehicleSourceId, setVehicleSourceId] = useState("");
  const [minConfidence, setMinConfidence] = useState("");
  const [search, setSearch] = useState("");
  const [sources, setSources] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehicleDuplicateCandidates({
        matchStatus: matchStatus || undefined,
        reviewRequired: reviewRequired || undefined,
        vehicleSourceId: vehicleSourceId || undefined,
        minConfidence: minConfidence ? Number(minConfidence) : undefined,
        search: search.trim() || undefined,
        limit: 200,
      });
      setMatches(data.matches || []);
    } catch (error) {
      setMatches([]);
      setErrorMessage(error.message || "Unable to load duplicate candidates.");
    } finally {
      setLoading(false);
    }
  }, [matchStatus, reviewRequired, vehicleSourceId, minConfidence, search]);

  useEffect(() => {
    fetchVehicleSources({ sort: "source_name", sortDir: "asc" })
      .then((data) => setSources(data.sources || []))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirm = async (id) => {
    if (!canManage) return;
    try {
      await confirmIdentityMatch(id);
      setToast("Match confirmed.");
      await load();
    } catch (error) {
      setToast(error.message || "Confirm failed.");
    }
  };

  const handleReject = async (id) => {
    if (!canManage) return;
    try {
      await rejectIdentityMatch(id);
      setToast("Match rejected.");
      await load();
    } catch (error) {
      setToast(error.message || "Reject failed.");
    }
  };

  return (
    <div className="admin-dashboard admin-vehicle-duplicate-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">Vehicle Duplicate Review</h1>
          <p className="admin-dashboard__subtitle">
            Review proposed and manual-review duplicate matches across sources.
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={load}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-dashboard__toast">{toast}</p> : null}

      <section className="admin-dashboard__filters">
        <label>
          Status
          <select value={matchStatus} onChange={(e) => setMatchStatus(e.target.value)}>
            <option value="">All</option>
            <option value="PROPOSED">Proposed</option>
            <option value="MANUAL_REVIEW">Manual review</option>
            <option value="AUTO_CONFIRMED">Auto confirmed</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </label>
        <label>
          Review required
          <select value={reviewRequired} onChange={(e) => setReviewRequired(e.target.value)}>
            <option value="">Any</option>
            <option value="true">Yes</option>
          </select>
        </label>
        <label>
          Source
          <select value={vehicleSourceId} onChange={(e) => setVehicleSourceId(e.target.value)}>
            <option value="">All</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sourceName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Min score
          <input
            type="number"
            min="0"
            max="100"
            value={minConfidence}
            onChange={(e) => setMinConfidence(e.target.value)}
          />
        </label>
        <label>
          Search
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Make, model, URL" />
        </label>
      </section>

      {loading ? (
        <AdminStatePanel message="Loading duplicate candidates…" />
      ) : errorMessage ? (
        <AdminStatePanel message={errorMessage} variant="error" />
      ) : (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Score</th>
                <th>Status</th>
                <th>Method</th>
                <th>Matched</th>
                <th>Conflicts</th>
                <th>Review</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div>
                      <strong>
                        {m.candidateMake} {m.candidateModel} {m.candidateYear}
                      </strong>
                      <div className="admin-table__meta">{m.candidateSourceName}</div>
                    </div>
                  </td>
                  <td>{m.matchConfidence}</td>
                  <td>{m.matchStatus}</td>
                  <td>{m.matchMethod}</td>
                  <td>
                    {Array.isArray(m.matchedFields?.fields)
                      ? m.matchedFields.fields.join(", ")
                      : "—"}
                  </td>
                  <td>
                    {Array.isArray(m.conflictingFields) && m.conflictingFields.length
                      ? m.conflictingFields.join(", ")
                      : "—"}
                  </td>
                  <td>{m.reviewRequired ? "Yes" : "No"}</td>
                  <td>{formatDate(m.createdAt)}</td>
                  <td>
                    <RowActionMenu
                      actions={[
                        canManage && m.matchStatus !== "CONFIRMED" && m.matchStatus !== "AUTO_CONFIRMED"
                          ? { label: "Confirm", onClick: () => handleConfirm(m.id) }
                          : null,
                        canManage && m.matchStatus !== "REJECTED"
                          ? { label: "Reject", onClick: () => handleReject(m.id) }
                          : null,
                        onOpenListing
                          ? {
                              label: "Open listing",
                              onClick: () => onOpenListing(m.vehicleListingId),
                            }
                          : null,
                        onOpenCanonical
                          ? {
                              label: "Open canonical",
                              onClick: () => onOpenCanonical(m.canonicalVehicleId),
                            }
                          : null,
                      ].filter(Boolean)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!matches.length ? <p className="admin-table__empty">No duplicate matches found.</p> : null}
        </AdminTableContainer>
      )}
    </div>
  );
};

export default VehicleDuplicateReviewPage;
